import * as Api from '@ViewModels';
import * as React from 'react';
import { EventLogger } from '../../../extViewmodels';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { toCoachMessage } from './coachMessage';
import { COACH_ASSISTANT_NAME } from './prompts';
import type { CoachMessage, CoachUpstreamMessage } from './types';
import {
	type CoachStreamVariables,
	finalizeReply,
	nowId,
	useCoachConversationStream,
} from './useCoachConversationStream';

const RECENT_CALLS_FEEDBACK_SOURCE = 'milestone-feedback';
const NOT_ENOUGH_SCORED_CALLS_PREFIX = 'NotEnoughScoredCalls';

/**
 * `idle` before the first review, `loading` until the first reply token lands, `ready` once the
 * review is streaming or done, `notEnoughCalls` when the server refuses for lack of scored calls.
 * Only the review request drives the phase; a failed follow-up sets `followUpFailed` instead.
 */
export type RecentCallsFeedbackPhase = 'idle' | 'loading' | 'ready' | 'notEnoughCalls' | 'error';

export type UseRecentCallsFeedbackApi = {
	messages: CoachMessage[];
	phase: RecentCallsFeedbackPhase;
	isResponding: boolean;
	/** True when the latest follow-up got no reply; cleared by the next `send` or `start`. */
	followUpFailed: boolean;
	/** Start a fresh review in a new conversation, abandoning any previous one. */
	start(): Promise<void>;
	/** Start a fresh review unless one is still being produced, which is left to finish. */
	requestReview(): void;
	/** Ask a follow-up in the active review conversation. */
	send(text: string): void;
};

type ReviewState = 'idle' | 'creating' | 'pending' | 'notEnoughCalls' | 'failed';

// The server sends the error as a JSON-encoded string, e.g. `"NotEnoughScoredCalls: ..."`.
const parseStreamErrorData = (data: string): string => {
	try {
		const parsed: unknown = JSON.parse(data);
		return typeof parsed === 'string' ? parsed : data;
	} catch {
		return data;
	}
};

export const isNotEnoughScoredCallsError = (data: string): boolean =>
	parseStreamErrorData(data).startsWith(NOT_ENOUGH_SCORED_CALLS_PREFIX);

/**
 * Reviews the rep's recent scored calls in a company-less `SalesLiveCoachingAssistant`
 * conversation. The server picks the calls and owns the review prompt; the client only sends
 * `CoachRecentCallsReviewAction`. The conversation id lives in hook state only — never in the
 * lead `conversationCache`, or the lead's coaching chat would resume into the review thread and
 * keyword pins would fire into it.
 */
export function useRecentCallsFeedback(): UseRecentCallsFeedbackApi {
	const [upstream, setUpstream] = React.useState<CoachUpstreamMessage[]>([]);
	const [reviewState, setReviewState] = React.useState<ReviewState>('idle');
	const [followUpFailed, setFollowUpFailed] = React.useState(false);
	const conversationIdRef = React.useRef<string | null>(null);
	const reviewRequestIdRef = React.useRef<string | null>(null);
	// The review and its follow-ups share one stream, so this says which of them the stream is serving.
	const streamingRequestIdRef = React.useRef<string | null>(null);
	const repliedRequestIdsRef = React.useRef(new Set<string>());
	const sessionRef = React.useRef(0);

	const createMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			EventLogger.logEvent({ category: 'FocusedViewCoaching', action: 'RecentCallsFeedbackCreate' }, { error });
		},
	});

	const failRequest = (variables: CoachStreamVariables, outcome: 'notEnoughCalls' | 'failed' = 'failed') => {
		if (variables.requestId === reviewRequestIdRef.current) {
			setReviewState(prev => (prev === 'pending' ? outcome : prev));
		} else {
			setFollowUpFailed(true);
		}
	};

	const stream = useCoachConversationStream({
		conversationIdRef,
		setMessages: setUpstream,
		onReply: variables => repliedRequestIdsRef.current.add(variables.requestId),
		onOtherEvent: (event, variables) => {
			if (event.event !== 'Error') return;
			failRequest(variables, isNotEnoughScoredCallsError(event.data) ? 'notEnoughCalls' : 'failed');
		},
		onSuccess: variables => {
			if (!repliedRequestIdsRef.current.has(variables.requestId)) failRequest(variables);
		},
		onError: (_error, variables) => {
			failRequest(variables);
			setUpstream(prev => finalizeReply(prev, variables.requestId));
		},
	});

	const start = async () => {
		stream.reset();
		const session = ++sessionRef.current;
		conversationIdRef.current = null;
		reviewRequestIdRef.current = null;
		repliedRequestIdsRef.current = new Set();
		setUpstream([]);
		setReviewState('creating');
		setFollowUpFailed(false);
		let conversation: Api.AIConversation | undefined;
		try {
			conversation = await createMutation.mutateAsync({
				assistantName: COACH_ASSISTANT_NAME,
				source: RECENT_CALLS_FEEDBACK_SOURCE,
			});
		} catch {
			// Logged by the mutation's onError; a superseded start must not fail the newer review.
		}
		if (session !== sessionRef.current) return;
		if (!conversation?.id) {
			setReviewState('failed');
			return;
		}
		setReviewState('pending');
		conversationIdRef.current = conversation.id;
		const requestId = nowId('req');
		reviewRequestIdRef.current = requestId;
		streamingRequestIdRef.current = requestId;
		const action = { _type: 'CoachRecentCallsReviewAction' } as Api.CoachRecentCallsReviewAction;
		void stream.mutate({
			convoId: conversation.id,
			requestId,
			chatRequest: { _type: 'AIActionRequest', action } as Api.AIActionRequest,
		});
	};

	const send = (text: string) => {
		const convoId = conversationIdRef.current;
		const trimmed = text.trim();
		if (!convoId || !trimmed) return;
		setFollowUpFailed(false);
		setUpstream(prev => [
			...prev,
			{ id: nowId('question'), text: trimmed, timestamp: new Date(), isStreaming: false, role: 'user' },
		]);
		const requestId = nowId('req');
		streamingRequestIdRef.current = requestId;
		void stream.mutate({
			convoId,
			requestId,
			chatRequest: { chatMessages: [{ role: Api.AIChatRole.User, text: trimmed, id: null }] } as Api.AIMessageRequest,
		});
	};

	const hasReviewReply = upstream.some(m => m.role === 'assistant');
	const phase: RecentCallsFeedbackPhase =
		reviewState === 'idle'
			? 'idle'
			: reviewState === 'failed'
				? 'error'
				: reviewState === 'notEnoughCalls'
					? 'notEnoughCalls'
					: reviewState === 'pending' && hasReviewReply
						? 'ready'
						: 'loading';

	const isResponding = phase === 'loading' || stream.isLoading || stream.isStreaming;

	return {
		messages: upstream.map(m => toCoachMessage(m)),
		phase,
		isResponding,
		followUpFailed,
		start,
		requestReview: () => {
			const reviewStreaming =
				(stream.isLoading || stream.isStreaming) && streamingRequestIdRef.current === reviewRequestIdRef.current;
			if (phase !== 'loading' && !reviewStreaming) void start();
		},
		send,
	};
}
